import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Plus, 
  Trash2, 
  Pencil, 
  Save, 
  X,
  ListChecks,
  Briefcase,
  ChevronDown,
  ChevronUp,
  Share2,
  MapPin,
  Loader2
} from "lucide-react";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { crmService } from "../crmApi";

// Types
interface ConfigItem {
  id: string;
  name: string;
}

const CRMConfig = () => {
  const { toast } = useToast();
  const [statuses, setStatuses] = React.useState<ConfigItem[]>([]);
  const [programs, setPrograms] = React.useState<ConfigItem[]>([]);
  const [leadSources, setLeadSources] = React.useState<ConfigItem[]>([]);
  const [addressCategories, setAddressCategories] = React.useState<ConfigItem[]>([]);
  const [baps, setBaps] = React.useState<ConfigItem[]>([]);
  
  const [newStatus, setNewStatus] = React.useState("");
  const [newProgram, setNewProgram] = React.useState("");
  const [newLeadSource, setNewLeadSource] = React.useState("");
  const [newAddressCategory, setNewAddressCategory] = React.useState("");
  const [newBap, setNewBap] = React.useState("");
  
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editingValue, setEditingValue] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);

  const [isStatusCollapsed, setIsStatusCollapsed] = React.useState(false);
  const [isProgramCollapsed, setIsProgramCollapsed] = React.useState(false);
  const [isLeadSourceCollapsed, setIsLeadSourceCollapsed] = React.useState(false);
  const [isAddressCategoryCollapsed, setIsAddressCategoryCollapsed] = React.useState(false);
  const [isBapCollapsed, setIsBapCollapsed] = React.useState(false);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [sRes, pRes, lRes, aRes, bRes] = await Promise.all([
        crmService.getStatuses(),
        crmService.getPrograms(),
        crmService.getLeadSources(),
        crmService.getAddressCategories(),
        crmService.getBaps()
      ]);
      setStatuses(sRes.data);
      setPrograms(pRes.data);
      setLeadSources(lRes.data);
      setAddressCategories(aRes.data);
      setBaps(bRes.data);
    } catch (error) {
      toast({ title: "Error fetching config", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    fetchData();
  }, []);

  // Status handlers
  const addStatus = async () => {
    if (!newStatus.trim()) return;
    try {
      await crmService.createStatus({ name: newStatus.trim() });
      setNewStatus("");
      fetchData();
      toast({ title: "Status added" });
    } catch (error) {
      toast({ title: "Failed to add status", variant: "destructive" });
    }
  };

  const deleteStatus = async (id: string) => {
    try {
      await crmService.deleteStatus(id);
      fetchData();
      toast({ title: "Status removed", variant: "destructive" });
    } catch (error) {
      toast({ title: "Failed to remove status", variant: "destructive" });
    }
  };

  // Program handlers
  const addProgram = async () => {
    if (!newProgram.trim()) return;
    try {
      await crmService.createProgram({ name: newProgram.trim() });
      setNewProgram("");
      fetchData();
      toast({ title: "Program added" });
    } catch (error) {
      toast({ title: "Failed to add program", variant: "destructive" });
    }
  };

  const deleteProgram = async (id: string) => {
    try {
      await crmService.deleteProgram(id);
      fetchData();
      toast({ title: "Program removed", variant: "destructive" });
    } catch (error) {
      toast({ title: "Failed to remove program", variant: "destructive" });
    }
  };

  // Lead Source handlers
  const addLeadSource = async () => {
    if (!newLeadSource.trim()) return;
    try {
      await crmService.createLeadSource({ name: newLeadSource.trim() });
      setNewLeadSource("");
      fetchData();
      toast({ title: "Lead source added" });
    } catch (error) {
      toast({ title: "Failed to add lead source", variant: "destructive" });
    }
  };

  const deleteLeadSource = async (id: string) => {
    try {
      await crmService.deleteLeadSource(id);
      fetchData();
      toast({ title: "Lead source removed", variant: "destructive" });
    } catch (error) {
      toast({ title: "Failed to remove lead source", variant: "destructive" });
    }
  };

  // Address Category handlers
  const addAddressCategory = async () => {
    if (!newAddressCategory.trim()) return;
    try {
      await crmService.createAddressCategory({ name: newAddressCategory.trim() });
      setNewAddressCategory("");
      fetchData();
      toast({ title: "Address category added" });
    } catch (error) {
      toast({ title: "Failed to add address category", variant: "destructive" });
    }
  };

  const deleteAddressCategory = async (id: string) => {
    try {
      await crmService.deleteAddressCategory(id);
      fetchData();
      toast({ title: "Address category removed", variant: "destructive" });
    } catch (error) {
      toast({ title: "Failed to remove address category", variant: "destructive" });
    }
  };

  const startEditing = (item: ConfigItem) => {
    setEditingId(item.id);
    setEditingValue(item.name);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingValue("");
  };

  const saveEdit = async (type: "status" | "program" | "lead-source" | "address-category" | "bap") => {
    if (!editingValue.trim() || !editingId) return;
    
    try {
      if (type === "status") await crmService.updateStatus(editingId, { name: editingValue.trim() });
      else if (type === "program") await crmService.updateProgram(editingId, { name: editingValue.trim() });
      else if (type === "lead-source") await crmService.updateLeadSource(editingId, { name: editingValue.trim() });
      else if (type === "address-category") await crmService.updateAddressCategory(editingId, { name: editingValue.trim() });
      else if (type === "bap") await crmService.updateBap(editingId, { name: editingValue.trim() });
      
      setEditingId(null);
      setEditingValue("");
      fetchData();
      toast({ title: "Updated successfully" });
    } catch (error) {
      toast({ title: "Update failed", variant: "destructive" });
    }
  };

  // BAP handlers
  const addBap = async () => {
    if (!newBap.trim()) return;
    try {
      await crmService.createBap({ name: newBap.trim() });
      setNewBap("");
      fetchData();
      toast({ title: "BAP added" });
    } catch (error) {
      toast({ title: "Failed to add BAP", variant: "destructive" });
    }
  };

  const deleteBap = async (id: string) => {
    try {
      await crmService.deleteBap(id);
      fetchData();
      toast({ title: "BAP removed", variant: "destructive" });
    } catch (error) {
      toast({ title: "Failed to remove BAP", variant: "destructive" });
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-7xl mx-auto pb-10 px-4">
      {isLoading && (
        <div className="fixed inset-0 bg-white/50 backdrop-blur-sm flex items-center justify-center z-50">
          <Loader2 className="h-8 w-8 animate-spin text-intellend-green" />
        </div>
      )}
      {/* Lead Statuses Section */}
      <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden h-fit">
        <div 
          className="p-6 border-b border-gray-100 bg-slate-50/50 flex items-center justify-between cursor-pointer hover:bg-slate-100 transition-colors"
          onClick={() => setIsStatusCollapsed(!isStatusCollapsed)}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-intellend-green/10 rounded-lg">
              <ListChecks className="h-5 w-5 text-intellend-green" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Lead Statuses</h3>
              <p className="text-sm text-gray-500">Manage lifecycle stages</p>
            </div>
          </div>
          {isStatusCollapsed ? <ChevronDown className="h-5 w-5 text-gray-400" /> : <ChevronUp className="h-5 w-5 text-gray-400" />}
        </div>
        
        {!isStatusCollapsed && (
          <div className="p-6 space-y-6 animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="flex gap-3">
              <Input 
                placeholder="New status..." 
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addStatus()}
                className="flex-1"
              />
              <Button onClick={addStatus} size="sm" className="bg-intellend-green hover:bg-intellend-green/90 text-white whitespace-nowrap">
                <Plus className="h-4 w-4 mr-2" /> Add
              </Button>
            </div>

            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/50">
                    <TableHead>Status Name</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {statuses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} className="text-center py-8 text-gray-500 italic">
                        No statuses defined.
                      </TableCell>
                    </TableRow>
                  ) : (
                    statuses.map((status) => (
                      <TableRow key={status.id}>
                        <TableCell className="font-medium">
                          {editingId === status.id ? (
                            <Input 
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              className="h-8"
                            />
                          ) : (
                            status.name
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {editingId === status.id ? (
                              <>
                                <Button size="icon" variant="ghost" onClick={() => saveEdit("status")} className="h-8 w-8 text-intellend-green">
                                  <Save className="h-4 w-4" />
                                </Button>
                                <Button size="icon" variant="ghost" onClick={cancelEditing} className="h-8 w-8 text-gray-400">
                                  <X className="h-4 w-4" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button size="icon" variant="ghost" onClick={() => startEditing(status)} className="h-8 w-8 text-blue-500">
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button size="icon" variant="ghost" onClick={() => deleteStatus(status.id)} className="h-8 w-8 text-red-500">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </section>

      {/* Program Names Section */}
      <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden h-fit">
        <div 
          className="p-6 border-b border-gray-100 bg-slate-50/50 flex items-center justify-between cursor-pointer hover:bg-slate-100 transition-colors"
          onClick={() => setIsProgramCollapsed(!isProgramCollapsed)}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-intellend-green/10 rounded-lg">
              <Briefcase className="h-5 w-5 text-intellend-green" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Program Names</h3>
              <p className="text-sm text-gray-500">Manage financial programs</p>
            </div>
          </div>
          {isProgramCollapsed ? <ChevronDown className="h-5 w-5 text-gray-400" /> : <ChevronUp className="h-5 w-5 text-gray-400" />}
        </div>
        
        {!isProgramCollapsed && (
          <div className="p-6 space-y-6 animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="flex gap-3">
              <Input 
                placeholder="New program..." 
                value={newProgram}
                onChange={(e) => setNewProgram(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addProgram()}
                className="flex-1"
              />
              <Button onClick={addProgram} size="sm" className="bg-intellend-green hover:bg-intellend-green/90 text-white whitespace-nowrap">
                <Plus className="h-4 w-4 mr-2" /> Add
              </Button>
            </div>

            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/50">
                    <TableHead>Program Name</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {programs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} className="text-center py-8 text-gray-500 italic">
                        No programs defined.
                      </TableCell>
                    </TableRow>
                  ) : (
                    programs.map((program) => (
                      <TableRow key={program.id}>
                        <TableCell className="font-medium">
                          {editingId === program.id ? (
                            <Input 
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              className="h-8"
                            />
                          ) : (
                            program.name
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {editingId === program.id ? (
                              <>
                                <Button size="icon" variant="ghost" onClick={() => saveEdit("program")} className="h-8 w-8 text-intellend-green">
                                  <Save className="h-4 w-4" />
                                </Button>
                                <Button size="icon" variant="ghost" onClick={cancelEditing} className="h-8 w-8 text-gray-400">
                                  <X className="h-4 w-4" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button size="icon" variant="ghost" onClick={() => startEditing(program)} className="h-8 w-8 text-blue-500">
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button size="icon" variant="ghost" onClick={() => deleteProgram(program.id)} className="h-8 w-8 text-red-500">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </section>

      {/* Lead Sources Section */}
      <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden h-fit">
        <div 
          className="p-6 border-b border-gray-100 bg-slate-50/50 flex items-center justify-between cursor-pointer hover:bg-slate-100 transition-colors"
          onClick={() => setIsLeadSourceCollapsed(!isLeadSourceCollapsed)}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-500/10 rounded-lg">
              <Share2 className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Lead Sources</h3>
              <p className="text-sm text-gray-500">Manage where leads come from</p>
            </div>
          </div>
          {isLeadSourceCollapsed ? <ChevronDown className="h-5 w-5 text-gray-400" /> : <ChevronUp className="h-5 w-5 text-gray-400" />}
        </div>
        
        {!isLeadSourceCollapsed && (
          <div className="p-6 space-y-6 animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="flex gap-3">
              <Input 
                placeholder="New lead source..." 
                value={newLeadSource}
                onChange={(e) => setNewLeadSource(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addLeadSource()}
                className="flex-1"
              />
              <Button onClick={addLeadSource} size="sm" className="bg-intellend-green hover:bg-intellend-green/90 text-white whitespace-nowrap">
                <Plus className="h-4 w-4 mr-2" /> Add
              </Button>
            </div>

            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/50">
                    <TableHead>Source Name</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leadSources.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} className="text-center py-8 text-gray-500 italic">
                        No lead sources defined.
                      </TableCell>
                    </TableRow>
                  ) : (
                    leadSources.map((source) => (
                      <TableRow key={source.id}>
                        <TableCell className="font-medium">
                          {editingId === source.id ? (
                            <Input 
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              className="h-8"
                            />
                          ) : (
                            source.name
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {editingId === source.id ? (
                              <>
                                <Button size="icon" variant="ghost" className="h-8 w-8 text-intellend-green" onClick={() => saveEdit("lead-source")}>
                                  <Save className="h-4 w-4" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-8 w-8 text-gray-400" onClick={cancelEditing}>
                                  <X className="h-4 w-4" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button size="icon" variant="ghost" className="h-8 w-8 text-blue-500" onClick={() => startEditing(source)}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => deleteLeadSource(source.id)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </section>

      {/* Address Categories Section */}
      <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden h-fit">
        <div 
          className="p-6 border-b border-gray-100 bg-slate-50/50 flex items-center justify-between cursor-pointer hover:bg-slate-100 transition-colors"
          onClick={() => setIsAddressCategoryCollapsed(!isAddressCategoryCollapsed)}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/10 rounded-lg">
              <MapPin className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Address Categories</h3>
              <p className="text-sm text-gray-500">Home, Office, Shop, etc.</p>
            </div>
          </div>
          {isAddressCategoryCollapsed ? <ChevronDown className="h-5 w-5 text-gray-400" /> : <ChevronUp className="h-5 w-5 text-gray-400" />}
        </div>
        
        {!isAddressCategoryCollapsed && (
          <div className="p-6 space-y-6 animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="flex gap-3">
              <Input 
                placeholder="New category..." 
                value={newAddressCategory}
                onChange={(e) => setNewAddressCategory(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addAddressCategory()}
                className="flex-1"
              />
              <Button onClick={addAddressCategory} size="sm" className="bg-intellend-green hover:bg-intellend-green/90 text-white whitespace-nowrap">
                <Plus className="h-4 w-4 mr-2" /> Add
              </Button>
            </div>

            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/50">
                    <TableHead>Category Name</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {addressCategories.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} className="text-center py-8 text-gray-500 italic">
                        No categories defined.
                      </TableCell>
                    </TableRow>
                  ) : (
                    addressCategories.map((cat) => (
                      <TableRow key={cat.id}>
                        <TableCell className="font-medium">
                          {editingId === cat.id ? (
                            <Input 
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              className="h-8"
                            />
                          ) : (
                            cat.name
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {editingId === cat.id ? (
                              <>
                                <Button size="icon" variant="ghost" className="h-8 w-8 text-intellend-green" onClick={() => saveEdit("address-category")}>
                                  <Save className="h-4 w-4" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-8 w-8 text-gray-400" onClick={cancelEditing}>
                                  <X className="h-4 w-4" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button size="icon" variant="ghost" className="h-8 w-8 text-blue-500" onClick={() => startEditing(cat)}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => deleteAddressCategory(cat.id)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </section>

      {/* BAP Names Section */}
      <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden h-fit">
        <div 
          className="p-6 border-b border-gray-100 bg-slate-50/50 flex items-center justify-between cursor-pointer hover:bg-slate-100 transition-colors"
          onClick={() => setIsBapCollapsed(!isBapCollapsed)}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/10 rounded-lg">
              <Plus className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">BAP Names</h3>
              <p className="text-sm text-gray-500">Manage business partners</p>
            </div>
          </div>
          {isBapCollapsed ? <ChevronDown className="h-5 w-5 text-gray-400" /> : <ChevronUp className="h-5 w-5 text-gray-400" />}
        </div>
        
        {!isBapCollapsed && (
          <div className="p-6 space-y-6 animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="flex gap-3">
              <Input 
                placeholder="New BAP..." 
                value={newBap}
                onChange={(e) => setNewBap(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addBap()}
                className="flex-1"
              />
              <Button onClick={addBap} size="sm" className="bg-intellend-green hover:bg-intellend-green/90 text-white whitespace-nowrap">
                <Plus className="h-4 w-4 mr-2" /> Add
              </Button>
            </div>

            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/50">
                    <TableHead>BAP Name</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {baps.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} className="text-center py-8 text-gray-500 italic">
                        No BAPs defined.
                      </TableCell>
                    </TableRow>
                  ) : (
                    baps.map((bap) => (
                      <TableRow key={bap.id}>
                        <TableCell className="font-medium">
                          {editingId === bap.id ? (
                            <Input 
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              className="h-8"
                            />
                          ) : (
                            bap.name
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {editingId === bap.id ? (
                              <>
                                <Button size="icon" variant="ghost" className="h-8 w-8 text-intellend-green" onClick={() => saveEdit("bap")}>
                                  <Save className="h-4 w-4" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-8 w-8 text-gray-400" onClick={cancelEditing}>
                                  <X className="h-4 w-4" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button size="icon" variant="ghost" className="h-8 w-8 text-blue-500" onClick={() => startEditing(bap)}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => deleteBap(bap.id)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default CRMConfig;
